import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ChevronLeftIcon, DocumentIcon } from "@/assets/icons";
import { CompanyDetailActions, CompanyDetailFields, CompanyDetailHeader } from "@/components/admin/company-detail";
import { BenchmarksList } from "@/components";
import { routes, strings } from "@/constants";
import { AlertVariantEnum } from "@/enums";
import {
    useCompany,
    useConfirm,
    useDeleteCompany,
    useDisableCompany,
    useEnableCompany,
    useToast,
} from "@/hooks";

const placeholderCardClass = `
    px-6
    py-12
    text-center
    text-sm
    text-text-secondary
    bg-surface
    border
    border-gray-200
    rounded-default
`;

export const AdminCompanyDetailPage = () => {
    const { id } = useParams<{ id: string }>();

    const navigate = useNavigate();

    const { onShow: showToastHandler } = useToast();

    const { onAsk: askConfirmHandler } = useConfirm();

    const {
        data: company,
        isLoading,
        onRun: fetchCompanyHandler,
    } = useCompany();

    const {
        isLoading: isEnabling,
        onRun: runEnableHandler,
    } = useEnableCompany();

    const {
        isLoading: isDisabling,
        onRun: runDisableHandler,
    } = useDisableCompany();

    const {
        isLoading: isDeleting,
        onRun: runDeleteHandler,
    } = useDeleteCompany();

    const toggleStatusHandler = async () => {
        if (!company) return;

        const wantsDisable = company.isEnabled;

        const confirmed = await askConfirmHandler({
            confirmLabel: wantsDisable ? strings.admin.disableCompany : strings.admin.enableCompany,
            isDanger: wantsDisable,
            message: wantsDisable ? strings.admin.confirmDisableCompany : strings.admin.confirmEnableCompany,
            title: wantsDisable ? strings.admin.disableCompanyTitle : strings.admin.enableCompanyTitle,
        });

        if (!confirmed) return;

        try {
            if (wantsDisable) await runDisableHandler(company.id);
            else await runEnableHandler(company.id);

            await fetchCompanyHandler(company.id);

            showToastHandler({
                message: wantsDisable ? strings.admin.disableCompanySuccess : strings.admin.enableCompanySuccess,
                variant: AlertVariantEnum.SUCCESS,
            });
        } catch (error) {
            showToastHandler({
                message: error instanceof Error ? error.message : strings.errors.tryAgain,
                variant: AlertVariantEnum.ERROR,
            });
        }
    };

    const deleteHandler = async () => {
        if (!company) return;

        const confirmed = await askConfirmHandler({
            confirmLabel: strings.admin.deleteCompany,
            isDanger: true,
            message: strings.admin.confirmDeleteCompany,
            title: strings.admin.deleteCompanyTitle,
        });

        if (!confirmed) return;

        try {
            await runDeleteHandler(company.id);

            showToastHandler({
                message: strings.admin.deleteCompanySuccess,
                variant: AlertVariantEnum.SUCCESS,
            });

            navigate(routes.adminCompanies);
        } catch (error) {
            showToastHandler({
                message: error instanceof Error ? error.message : strings.errors.tryAgain,
                variant: AlertVariantEnum.ERROR,
            });
        }
    };

    useEffect(
        () => {
            if (id) fetchCompanyHandler(id);
        },
        [id, fetchCompanyHandler],
    );

    return (
        <div
            className="
                mx-auto
                w-full
                max-w-4xl
                space-y-6
            "
        >
            <Link
                to={routes.adminCompanies}
                className="
                    inline-flex
                    items-center
                    gap-1
                    text-sm
                    font-medium
                    text-primary
                    transition-colors
                    hover:text-primary-dark
                "
            >
                <ChevronLeftIcon className="h-4 w-4" />
                {strings.admin.backToCompanies}
            </Link>
            {isLoading ? <div className={placeholderCardClass}>{strings.admin.loading}</div> : !company ? (
                <div className={placeholderCardClass}>{strings.admin.emptyCompanies}</div>
            ) : (
                <>
                    <div
                        className="
                            overflow-hidden
                            rounded-default
                            border
                            border-gray-200
                            bg-surface
                            shadow-sm
                        "
                    >
                        <CompanyDetailHeader company={company} />
                        <CompanyDetailFields company={company} />
                        <CompanyDetailActions
                            isDeleting={isDeleting}
                            isEnabled={company.isEnabled}
                            isToggling={isEnabling || isDisabling}
                            onDelete={deleteHandler}
                            onToggle={toggleStatusHandler}
                        />
                    </div>
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <DocumentIcon className="h-5 w-5 shrink-0 text-primary sm:h-6 sm:w-6" />
                            <h2 className="text-lg font-bold text-text-primary sm:text-xl">{strings.benchmarks.title}</h2>
                        </div>
                        <BenchmarksList items={company.benchmarks ?? []} />
                    </div>
                </>
            )}
        </div>
    );
};
